import React, { useMemo } from 'react';
import './AmenitiesSection.css';

export const AMENITY_GROUPS = [
    {
        key: 'essential',
        title: 'Essential',
        description: 'Core amenities guests usually expect',
        icon: '⭐',
        items: [
            'WiFi',
            'Air Conditioning',
            'Heating',
            'Hot Water',
            'Power Backup',
            'Parking',
            'Housekeeping',
            'Reception'
        ]
    },
    {
        key: 'safety',
        title: 'Safety & Security',
        description: 'Amenities that improve guest safety',
        icon: '🛡️',
        items: [
            'CCTV',
            'Fire Extinguisher',
            'Smoke Detector',
            'First Aid Kit',
            'Security',
            'Emergency Exit',
            '24x7 Security'
        ]
    },
    {
        key: 'kitchen',
        title: 'Kitchen & Dining',
        description: 'Cooking and dining facilities',
        icon: '🍽️',
        items: [
            'Kitchen',
            'Refrigerator',
            'Microwave',
            'Stove',
            'Oven',
            'Dining Table',
            'Electric Kettle',
            'Coffee Maker',
            'Room Service'
        ]
    },
    {
        key: 'outdoor',
        title: 'Outdoor',
        description: 'Outdoor spaces and activities',
        icon: '🌿',
        items: [
            'Garden',
            'Swimming Pool',
            'Private Pool',
            'BBQ',
            'Bonfire',
            'Outdoor Seating',
            'Balcony',
            'Terrace',
            'Parking'
        ]
    },
    {
        key: 'entertainment',
        title: 'Entertainment',
        description: 'Things guests can enjoy during their stay',
        icon: '🎮',
        items: ['TV', 'Netflix', 'Music System', 'Games', 'Board Games', 'Indoor Games', 'Outdoor Games', 'Books']
    },
    {
        key: 'other',
        title: 'Other Amenities',
        description: 'Additional facilities and conveniences',
        icon: '✨',
        items: [
            'Laundry',
            'Iron',
            'Hair Dryer',
            'Workspace',
            'Lift',
            'Wheelchair Accessible',
            'Pet Friendly',
            'Smoking Area',
            'EV Charging'
        ]
    }
];

const normalizeAmenities = (amenities = {}) => ({
    essential: Array.isArray(amenities.essential) ? amenities.essential : [],
    safety: Array.isArray(amenities.safety) ? amenities.safety : [],
    kitchen: Array.isArray(amenities.kitchen) ? amenities.kitchen : [],
    outdoor: Array.isArray(amenities.outdoor) ? amenities.outdoor : [],
    entertainment: Array.isArray(amenities.entertainment) ? amenities.entertainment : [],
    other: Array.isArray(amenities.other) ? amenities.other : []
});

const AmenitiesSection = ({ formData, updateField, canEdit = true }) => {
    const amenities = useMemo(() => normalizeAmenities(formData?.amenities), [formData?.amenities]);

    const totalSelected = Object.values(amenities).reduce((total, items) => total + items.length, 0);

    const updateAmenities = (groupKey, values) => {
        if (!canEdit) return;

        updateField('amenities', {
            ...amenities,
            [groupKey]: values
        });
    };

    const toggleAmenity = (groupKey, item) => {
        if (!canEdit) return;

        const currentItems = amenities[groupKey] || [];

        const exists = currentItems.includes(item);

        const updatedItems = exists ? currentItems.filter((value) => value !== item) : [...currentItems, item];

        updateAmenities(groupKey, updatedItems);
    };

    const selectAll = (group) => {
        if (!canEdit) return;

        updateAmenities(group.key, [...group.items]);
    };

    const clearGroup = (group) => {
        if (!canEdit) return;

        updateAmenities(group.key, []);
    };

    const clearAll = () => {
        if (!canEdit) return;

        updateField('amenities', {
            essential: [],
            safety: [],
            kitchen: [],
            outdoor: [],
            entertainment: [],
            other: []
        });
    };

    return (
        <div className='amenities-section'>
            {/* Header */}
            <div className='amenities-header'>
                <div>
                    <span className='section-eyebrow'>PROPERTY FACILITIES</span>

                    <h2>Amenities</h2>

                    <p>
                        Select the facilities and amenities available at this property. These will be displayed to
                        guests on the property page.
                    </p>
                </div>

                <div className='amenities-header-actions'>
                    <div className='amenities-count'>
                        <strong>{totalSelected}</strong>
                        <span>selected</span>
                    </div>

                    {canEdit && totalSelected > 0 && (
                        <button type='button' className='clear-all-btn' onClick={clearAll}>
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            {/* Selected summary */}
            {totalSelected > 0 && (
                <div className='selected-amenities'>
                    <div className='selected-amenities-header'>
                        <div>
                            <h3>Selected Amenities</h3>
                            <span>{totalSelected} amenities selected for this property</span>
                        </div>
                    </div>

                    <div className='selected-amenities-list'>
                        {Object.entries(amenities).map(([groupKey, items]) =>
                            items.map((item) => (
                                <span className='selected-amenity-tag' key={`${groupKey}-${item}`}>
                                    {item}

                                    {canEdit && (
                                        <button
                                            type='button'
                                            onClick={() => toggleAmenity(groupKey, item)}
                                            aria-label={`Remove ${item}`}
                                        >
                                            ×
                                        </button>
                                    )}
                                </span>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Amenity groups */}
            <div className='amenity-groups'>
                {AMENITY_GROUPS.map((group) => {
                    const selectedItems = amenities[group.key] || [];

                    const selectedCount = selectedItems.length;

                    const allSelected = selectedCount === group.items.length;

                    return (
                        <div className='amenity-group-card' key={group.key}>
                            {/* Group Header */}
                            <div className='amenity-group-header'>
                                <div className='amenity-group-title'>
                                    <div className='amenity-group-icon'>{group.icon}</div>

                                    <div>
                                        <h3>{group.title}</h3>

                                        <p>{group.description}</p>
                                    </div>
                                </div>

                                <div className='amenity-group-actions'>
                                    <span className='amenity-group-count'>
                                        {selectedCount}/{group.items.length}
                                    </span>

                                    {canEdit && (
                                        <>
                                            {!allSelected ? (
                                                <button type='button' onClick={() => selectAll(group)}>
                                                    Select all
                                                </button>
                                            ) : (
                                                <button type='button' onClick={() => clearGroup(group)}>
                                                    Clear
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Amenity Items */}
                            <div className='amenity-grid'>
                                {group.items.map((item) => {
                                    const selected = selectedItems.includes(item);

                                    return (
                                        <button
                                            type='button'
                                            key={item}
                                            className={`amenity-item ${selected ? 'selected' : ''}`}
                                            onClick={() => toggleAmenity(group.key, item)}
                                            disabled={!canEdit}
                                        >
                                            <span className={`amenity-checkbox ${selected ? 'checked' : ''}`}>
                                                {selected ? '✓' : ''}
                                            </span>

                                            <span className='amenity-item-name'>{item}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Custom amenities */}
            <CustomAmenities amenities={amenities} updateAmenities={updateAmenities} canEdit={canEdit} />

            {/* Info */}
            <div className='amenities-info'>
                <div className='amenities-info-icon'>💡</div>

                <div>
                    <strong>Tip</strong>

                    <p>
                        Only select amenities that are actually available to guests. Property-level amenities can be
                        overridden or supplemented by amenities configured for individual units.
                    </p>
                </div>
            </div>
        </div>
    );
};

const CustomAmenities = ({ amenities, updateAmenities, canEdit }) => {
    const [customAmenity, setCustomAmenity] = React.useState('');

    const customItems = amenities.other || [];

    const addCustomAmenity = () => {
        if (!canEdit) return;

        const value = customAmenity.trim();

        if (!value) return;

        const alreadyExists = customItems.some((item) => item.toLowerCase() === value.toLowerCase());

        if (alreadyExists) {
            setCustomAmenity('');
            return;
        }

        updateAmenities('other', [...customItems, value]);

        setCustomAmenity('');
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            addCustomAmenity();
        }
    };

    return (
        <div className='custom-amenities-card'>
            <div className='custom-amenities-header'>
                <div>
                    <h3>Custom Amenities</h3>

                    <p>Add an amenity that is not available in the list above.</p>
                </div>
            </div>

            <div className='custom-amenity-input-row'>
                <input
                    type='text'
                    value={customAmenity}
                    onChange={(event) => setCustomAmenity(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder='e.g. Private workspace'
                    disabled={!canEdit}
                />

                <button type='button' onClick={addCustomAmenity} disabled={!canEdit || !customAmenity.trim()}>
                    + Add
                </button>
            </div>

            <div className='custom-amenity-hint'>
                Press <strong>Enter</strong> to quickly add an amenity.
            </div>
        </div>
    );
};

export default AmenitiesSection;
